import React, { ReactNode } from 'react'
import classNames from 'classnames'

import { mq } from 'marketing-site/src/library/utils'
import { IProps } from './index'
import { HeadingBase } from '../../elements/HeadingBase'

const BODY_GAP = {
  default: {
    mobile: '1rem',
    tablet: '1.75rem',
    desktop: '2rem',
  },
  medium: {
    mobile: '1.5rem',
    tablet: '2rem',
    desktop: '2.5rem',
  },
  large: {
    mobile: '2rem',
    tablet: '2.5rem',
    desktop: '3rem',
  },
  huge: {
    mobile: '2.5rem',
    tablet: '3.5rem',
    desktop: '4rem',
  },
}

export const GridContainer: React.FC<IProps> = ({
  layout = '3-3',
  eyebrow,
  title,
  titleSize = 'heading-2',
  titleWidth = 'default',
  subhead,
  cta,
  ctaPosition = 'below-header',
  body,
  bodyAlignment = 'center',
  bodyColumnGap = 'default',
  bodyRowGap = 'default',
  theme = 'none',
  backgroundColor = 'transparent',
  brand,
}) => {
  const classes = classNames('grid-container', 'grid-layout', {
    [`grid-container--${layout}`]: layout,
    [`mvp-background-color-${backgroundColor}`]: backgroundColor !== 'transparent',
  })

  const headerClasses = classNames('grid-container__header', {
    [`grid-container__header--${titleWidth}`]: titleWidth,
  })

  const bodyClasses = classNames('grid-container__body', {
    [`grid-container__body--align-${bodyAlignment}`]: bodyAlignment,
  })

  return (
    <div className={classes} data-testid="grid-container">
      <div className="grid-container__inner">
        {(eyebrow || title) && (
          <div className={headerClasses}>
            <HeadingBase
              eyebrow={eyebrow}
              theme={theme}
              title={title}
              titleSize={titleSize}
              subhead={subhead}
              brand={brand}
            />
          </div>
        )}

        {cta &&
          (ctaPosition === 'below-header' || ctaPosition === 'below-header-and-below-body') && (
            <div className="grid-container__cta">{cta}</div>
          )}

        {body && (
          <div className={bodyClasses}>
            {React.Children.map(
              body,
              (item: ReactNode) =>
                item &&
                React.isValidElement(item) && (
                  <li className="grid-container__item">{React.cloneElement(item)}</li>
                ),
            )}
          </div>
        )}

        {cta && (ctaPosition === 'below-body' || ctaPosition === 'below-header-and-below-body') && (
          <div className="grid-container__cta">{cta}</div>
        )}
      </div>

      <style jsx>
        {`
          .grid-container {
            &__header,
            &__body,
            &__cta {
              grid-column: 1/-1;
            }

            &__header {
              text-align: center;
            }

            &__eyebrow {
              margin-bottom: 1rem;
            }

            * + &__subhead {
              margin-top: 1.5rem;
            }

            &__header + &__cta {
              margin-top: 2rem;
            }

            &__header + &__body,
            &__cta + &__body,
            &__body + &__cta {
              margin-top: 2.5rem;
            }

            &__cta {
              display: flex;
              flex-direction: column;
            }

            &__body {
              display: grid;
              grid-column-gap: ${BODY_GAP[bodyColumnGap].mobile};
              grid-row-gap: ${BODY_GAP[bodyRowGap].mobile};
            }

            &__item {
              width: 100%;

              // Remove additional padding from children.
              :global(& > section, & > div, & > section > div) {
                padding-top: 0;
                padding-bottom: 0;
              }
            }
          }

          @media (${mq.tablet}) {
            .grid-container {
              &__header {
                grid-column: 2/-2;
              }

              &__eyebrow {
                margin-bottom: 1.5rem;
              }

              &__header + &__cta {
                margin-top: 2.5rem;
              }

              &__header + &__body,
              &__cta + &__body,
              &__body + &__cta {
                margin-top: 3.5rem;
              }

              &__cta {
                flex-direction: row;
                justify-content: center;
              }

              &__body {
                grid-column-gap: ${BODY_GAP[bodyColumnGap].tablet};
                grid-row-gap: ${BODY_GAP[bodyRowGap].tablet};
              }

              &--2-2,
              &--2-2-full-width,
              &--2-3,
              &--3-3,
              &--4-4 {
                .grid-container__body {
                  grid-template-columns: repeat(4, 1fr);
                }

                .grid-container__item {
                  grid-column: span 2;
                }
              }

              &--2-2-full-width {
                .grid-container__body--align-center {
                  .grid-container__item {
                    &:last-child:nth-child(2n - 1) {
                      grid-column-end: 4;
                    }
                  }
                }
              }

              &--2-3 {
                .grid-container__body--align-center {
                  .grid-container__item {
                    &:nth-child(5n - 4),
                    &:nth-child(5n - 3),
                    &:nth-child(5n) {
                      grid-column: span 4;
                    }
                  }
                }
              }
            }
          }

          @media (${mq.tablet}) and (max-width: 1023px) {
            .grid-container {
              &--2-2,
              &--2-2-full-width,
              &--3-3,
              &--4-4 {
                .grid-container__body--align-center {
                  .grid-container__item {
                    &:last-child:nth-child(2n - 1) {
                      grid-column-end: 4;
                    }
                  }
                }
              }
            }
          }

          @media (${mq.desktop}) {
            .grid-container {
              &__header + &__body,
              &__cta + &__body,
              &__body + &__cta {
                margin-top: 4rem;
              }

              &__header {
                &--narrow {
                  grid-column: 4/-4;
                }

                &--default {
                  grid-column: 3/-3;
                }

                &--wide {
                  grid-column: 2/-2;
                }
              }

              &__body {
                grid-column-gap: ${BODY_GAP[bodyColumnGap].desktop};
                grid-row-gap: ${BODY_GAP[bodyRowGap].desktop};
              }

              &--2-2,
              &--2-3,
              &--3-3 {
                .grid-container__body {
                  grid-template-columns: repeat(6, 1fr);
                }
              }

              &--2-2 {
                .grid-container__body--align-center {
                  .grid-container__item {
                    &:nth-child(odd) {
                      grid-column-end: 4;
                    }

                    &:nth-child(even) {
                      grid-column-end: -2;
                    }

                    // Center single orphan in row.
                    &:last-child:nth-child(2n - 1) {
                      grid-column-end: 5;
                    }
                  }
                }
              }

              &--2-3 {
                .grid-container__body--align-center {
                  .grid-container__item {
                    grid-column: span 2;

                    &:nth-child(5n - 4),
                    &:nth-child(5n - 3) {
                      grid-column: span 3;
                    }

                    // Center single orphan in first row.
                    &:last-child:nth-child(5n - 4) {
                      grid-column-start: 2;
                      grid-column-end: -2;
                    }

                    // Center single orphan in second row.
                    &:last-child:nth-child(5n - 2) {
                      grid-column-end: 5;
                    }

                    // Center first of two orphans in second row.
                    &:nth-last-child(2):nth-child(5n - 2) {
                      grid-column-end: 4;
                    }

                    // Center second of two orphans in second row.
                    &:last-child:nth-child(5n - 1) {
                      grid-column-end: 6;
                    }

                    &:nth-child(5n) {
                      grid-column: span 2;
                    }
                  }
                }
              }

              &--3-3 {
                .grid-container__body--align-center {
                  .grid-container__item {
                    // Center single orphan in row.
                    &:last-child:nth-child(3n - 2) {
                      grid-column-end: 5;
                    }

                    // Center first of two orphans in row.
                    &:nth-last-child(2):nth-child(3n + 1) {
                      grid-column-end: 4;
                    }

                    // Center second of two orphans in row.
                    &:last-child:nth-child(3n - 1) {
                      grid-column-end: -2;
                    }
                  }
                }
              }

              &--4-4 {
                .grid-container__body {
                  grid-template-columns: repeat(8, 1fr);

                  &--align-center {
                    .grid-container__item {
                      // Center single orphan in row.
                      &:last-child:nth-child(4n - 3) {
                        grid-column-end: 6;
                      }

                      // Center two orphans in row.
                      &:nth-last-child(2):nth-child(4n + 1) {
                        grid-column-end: 5;
                      }

                      // Center three orphans in row.
                      &:nth-last-child(3):nth-child(4n + 1) {
                        grid-column-end: 4;
                      }
                    }
                  }
                }
              }
            }
          }
        `}
      </style>
    </div>
  )
}
